import { z } from 'zod';
import { PasswordRegex } from '@msdyn365-commerce-modules/retail-actions';

// See this Coda page for a mapped comparison of locale requirements:
// https://coda.io/d/Product-team_dEbHeHujlIi/Loyalty-per-country-specs-updated-on-2025-09-04_sudv2AgE?docIds=all#_luo9wPkC

export const getLoyaltySchema = (localeString: string, enableSignUp: boolean) => {
    // Helper function for phone number validation (supports +prefix)
    const validatePhone = (val?: string) => {
        if (!val || val.trim() === '') {
            return true;
        }
        // Check format: optional + at start followed by 4-16 digits
        if (!/^\+?\d+$/.test(val)) {
            return false;
        }
        const cleanPhone = val.replace(/^\+/, '');
        return cleanPhone.length >= 4 && cleanPhone.length <= 16;
    };

    // Helper function for birthday validation (allows empty strings)
    const validateBirthday = (val?: string) => {
        if (!val || val.trim() === '') {
            return true;
        }
        return /^(\d{4}[-\/]\d{1,2}[-\/]\d{1,2})|(\d{1,2}[-\/]\d{1,2}[-\/]\d{4})$/.test(val);
    };

    /**
     * BASE SCHEMA SPEC:
     * - Used for most EMEA, UK locales and as foundation for other schemas
     * - email: MANDATORY for all scenarios
     * - phone_number: OPTIONAL (can be empty string or undefined, validates 4-16 digits if provided)
     * - birthday: OPTIONAL (can be empty string or undefined)
     * - postal_code: OPTIONAL (can be empty string or undefined, validates max 9 chars if provided)
     * - marketing/profiling/thirdparty: OPTIONAL boolean flags
     * - country: OPTIONAL string
     */
    const baseSchema = z.object({
        country: z.string().optional(),
        email: z.string().email(),
        phone_number: z
            .string()
            .optional()
            .refine(validatePhone),
        marketing: z.boolean().optional(),
        profiling: z.boolean().optional(),
        thirdparty: z.boolean().optional(),
        birthday: z
            .string()
            .optional()
            .refine(validateBirthday),
        postal_code: z
            .string()
            .optional()
            .refine(val => !val || val?.trim() === '' || val?.length <= 9)
    });

    /**
     * SIGNUP SCHEMA SPEC (for enableSignUp=true):
     * - Extends baseSchema with additional MANDATORY fields for account creation
     * - name: MANDATORY (min 1 char)
     * - lastname: MANDATORY (min 1 char)
     * - password: MANDATORY (must meet MSD365 regex requirements + no currency symbols)
     * - Inherits all baseSchema fields with same optional/mandatory rules
     * - Used for most locales when enableSignUp=true
     */
    const signupSchema = baseSchema.extend({
        name: z.string().min(1),
        lastname: z.string().min(1),
        password: z
            .string()
            .regex(PasswordRegex.defaultRegex)
            .regex(/^[^\p{Sc}]*$/u)
    });

    /**
     * US SCHEMA SPEC (en-us locale, enableSignUp=false):
     * - Extends baseSchema with US-specific requirements
     * - name: MANDATORY (min 1 char)
     * - lastname: MANDATORY (min 1 char)
     * - phone_number: MANDATORY (4-16 digits, will be prefixed with +1 in submission)
     * - All other baseSchema fields remain optional
     */
    const usSchema = baseSchema.extend({
        name: z.string().min(1),
        lastname: z.string().min(1),
        phone_number: z
            .string()
            .min(4)
            .max(16)
    });

    /**
     * AU SCHEMA SPEC (en-au locale, enableSignUp=false):
     * - Extends baseSchema with Australia-specific requirements
     * - name: OPTIONAL (can be empty string)
     * - lastname: OPTIONAL (can be empty string)
     * - phone_number: MANDATORY (4-16 digits)
     * - birthday: MANDATORY (date string, min 1 char)
     * - Shows red asterisk (*) next to mandatory fields in UI
     */
    const auSchema = baseSchema.extend({
        name: z.string().optional(),
        lastname: z.string().optional(),
        phone_number: z
            .string()
            .min(4)
            .max(16),
        birthday: z.string().min(1)
    });

    /**
     * SCHEMA SELECTION LOGIC:
     * 1. If enableSignUp=true: Always use signupSchema (account creation flow)
     * 2. If enableSignUp=false: Use locale-specific schemas for loyalty enrollment only:
     *    - 'en-us': usSchema (mandatory phone, name, lastname)
     *    - 'en-au': auSchema (mandatory phone, birthday; optional name, lastname)
     *    - default: baseSchema (all fields optional except email)
     */
    if (enableSignUp === true) {
        console.log('using signupSchema');
        return signupSchema;
    }

    switch (localeString.toLowerCase()) {
        case 'en-us':
            console.log('using usSchema');
            return usSchema;
        case 'en-au':
            console.log('using auSchema');
            return auSchema;
        default:
            console.log('using baseSchema');
            return baseSchema;
    }
};
